import React, { useState, useMemo } from 'react';
import { Trophy, Filter, Lock, CheckCircle, Star } from 'lucide-react';
import AchievementBadge from './AchievementBadge';
import { ACHIEVEMENTS, Achievement } from '../../constants/gamification';
import { UserAchievement } from '../../constants/gamification';

interface AchievementGridProps {
  userAchievements: UserAchievement[];
  onAchievementClick?: (achievement: Achievement) => void;
  showFilters?: boolean;
  compact?: boolean;
}

const AchievementGrid: React.FC<AchievementGridProps> = ({
  userAchievements,
  onAchievementClick,
  showFilters = true,
  compact = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRarity, setSelectedRarity] = useState<string>('all');
  const [showOnlyUnlocked, setShowOnlyUnlocked] = useState(false);

  // Categories
  const categories = [
    { value: 'all', label: 'All Categories', icon: Trophy },
    { value: 'test_mastery', label: 'Test Mastery', icon: Trophy },
    { value: 'performance', label: 'Performance', icon: Star },
    { value: 'consistency', label: 'Consistency', icon: CheckCircle },
    { value: 'profile', label: 'Profile', icon: Trophy }
  ];

  // Rarities
  const rarities = [
    { value: 'all', label: 'All Rarities' },
    { value: 'common', label: 'Common' },
    { value: 'uncommon', label: 'Uncommon' },
    { value: 'rare', label: 'Rare' },
    { value: 'epic', label: 'Epic' },
    { value: 'legendary', label: 'Legendary' }
  ];

  // Check if achievement is unlocked
  const isAchievementUnlocked = (achievementId: string): UserAchievement | undefined => {
    return userAchievements.find(ua => ua.achievementId === achievementId);
  };

  // Filter achievements
  const filteredAchievements = useMemo(() => {
    return ACHIEVEMENTS.filter(achievement => {
      // Category filter
      if (selectedCategory !== 'all' && achievement.category !== selectedCategory) {
        return false;
      }

      // Rarity filter
      if (selectedRarity !== 'all' && achievement.rarity !== selectedRarity) {
        return false;
      }

      // Unlocked filter
      if (showOnlyUnlocked && !isAchievementUnlocked(achievement.id)) {
        return false;
      }

      return true;
    });
  }, [selectedCategory, selectedRarity, showOnlyUnlocked, userAchievements]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalAchievements = ACHIEVEMENTS.length;
    const unlockedCount = userAchievements.length;
    const totalPoints = userAchievements.reduce((sum, ua) => {
      const achievement = ACHIEVEMENTS.find(a => a.id === ua.achievementId);
      return sum + (achievement?.points || 0);
    }, 0);
    const percentage = Math.round((unlockedCount / totalAchievements) * 100);

    return { totalAchievements, unlockedCount, totalPoints, percentage };
  }, [userAchievements]);

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-6'}`}>
      {/* Header with Stats */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Trophy className="w-6 h-6 text-red-600" />
            <h2 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
              Achievements
            </h2>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-red-600">{stats.unlockedCount}/{stats.totalAchievements}</p>
            <p className="text-xs text-gray-500">Unlocked</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-500 to-red-600 transition-all duration-500 rounded-full"
            style={{ width: `${stats.percentage}%` }}
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-gray-600">{stats.percentage}% Complete</p>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-yellow-600">{stats.totalPoints.toLocaleString()}</span> points earned
          </p>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 space-y-4">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`
                      flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${selectedCategory === category.value
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{category.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rarity & Status Filters */}
          <div className="flex items-center space-x-4">
            {/* Rarity Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rarity
              </label>
              <select
                value={selectedRarity}
                onChange={(e) => setSelectedRarity(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {rarities.map(rarity => (
                  <option key={rarity.value} value={rarity.value}>
                    {rarity.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Show Only Unlocked Toggle */}
            <div className="flex items-center space-x-2 pt-7">
              <input
                type="checkbox"
                id="showUnlocked"
                checked={showOnlyUnlocked}
                onChange={(e) => setShowOnlyUnlocked(e.target.checked)}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label htmlFor="showUnlocked" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Unlocked Only
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Achievement Grid */}
      {filteredAchievements.length === 0 ? (
        <div className="text-center py-12">
          <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No achievements match your filters</p>
          <button
            onClick={() => {
              setSelectedCategory('all');
              setSelectedRarity('all');
              setShowOnlyUnlocked(false);
            }}
            className="mt-4 text-red-600 hover:text-red-700 font-medium text-sm"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
          {filteredAchievements.map(achievement => {
            const unlockedAchievement = isAchievementUnlocked(achievement.id);
            return (
              <AchievementBadge
                key={achievement.id}
                achievement={achievement}
                isUnlocked={!!unlockedAchievement}
                unlockedAt={unlockedAchievement?.unlockedAt}
                size="medium"
                showDetails={true}
                onClick={() => onAchievementClick?.(achievement)}
              />
            );
          })}
        </div>
      )}

      {/* Footer Tips */}
      {!compact && filteredAchievements.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-start space-x-2">
            <Trophy className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-900 mb-1">Achievement Tips:</p>
              <ul className="space-y-1 text-xs">
                <li>• Hover over achievements to see unlock requirements</li>
                <li>• Complete tests and maintain streaks to unlock more badges</li>
                <li>• Legendary achievements give the most points!</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementGrid;
